import { motion } from 'framer-motion';
import { Award, Heart, Shield, Star, MapPin, Phone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLang } from '@/context/LanguageContext';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6 } }),
};

const AboutPage = () => {
  const { t } = useLang();
  return (
    <div className="pt-20 min-h-screen" data-testid="about-page">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_fit-journey-app-11/artifacts/13xvuc6i_image00010.webp"
            alt="Coach Amira"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r rtl:bg-gradient-to-l from-[#08080A] via-[#08080A]/90 to-[#08080A]/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.p initial="hidden" animate="visible" variants={fadeUp} className="overline mb-4">
                {t('about.overline')}
              </motion.p>
              <motion.h1 initial="hidden" animate="visible" variants={fadeUp} custom={1}
                className="text-5xl lg:text-6xl font-light mb-8">
                {t('about.h_1')} <span className="font-bold gold-gradient-text">{t('about.h_2')}</span> {t('about.h_3')} <span className="font-bold">{t('about.h_4')}</span>
              </motion.h1>
              <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={2}
                className="text-[#A3A19C] text-lg leading-relaxed mb-6">
                {t('about.p1')}
              </motion.p>
              <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={3}
                className="text-[#A3A19C] leading-relaxed">
                {t('about.p2')}
              </motion.p>
            </div>
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={2}
              className="hidden lg:block">
              <div className="relative">
                <div className="w-full rounded-lg overflow-hidden border border-[rgba(212,175,55,0.2)]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_fit-journey-app-11/artifacts/13xvuc6i_image00010.webp"
                    alt="Coach Amira"
                    className="w-full h-auto block"
                  />
                </div>
                <div className="absolute -bottom-6 -start-6 bg-[#121215] border border-[rgba(212,175,55,0.2)] rounded-lg p-6">
                  <p className="text-3xl font-['Cormorant_Garamond'] font-bold gold-gradient-text">10+</p>
                  <p className="text-xs text-[#A3A19C] mt-1">{t('about.years')}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-[#121215] border-y border-[rgba(255,255,255,0.06)]" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="overline mb-4">{t('about.cred_overline')}</p>
          <h2 className="text-4xl font-light mb-16">{t('about.cred_1')} <span className="font-bold">{t('about.cred_2')}</span></h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: t('cert.ereps'), desc: t('about.ereps_desc'), icon: Award },
              { name: t('cert.ifa'), desc: t('about.ifa_desc'), icon: Shield },
              { name: t('cert.saudi_full'), desc: t('about.saudi_desc'), icon: Star },
            ].map((cert, i) => (
              <motion.div
                key={cert.name}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="bg-[#08080A] border border-[rgba(255,255,255,0.08)] rounded-lg p-8 card-hover"
                data-testid={`cert-card-${i}`}
              >
                <cert.icon className="w-8 h-8 text-[#D4AF37] mb-6" strokeWidth={1.5} />
                <h3 className="text-xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-3">{cert.name}</h3>
                <p className="text-sm text-[#A3A19C] leading-relaxed">{cert.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-24 lg:py-32" data-testid="philosophy-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="overline mb-4">{t('about.phil_overline')}</p>
              <h2 className="text-4xl font-light mb-8">{t('about.phil_1')} <span className="font-bold gold-gradient-text">{t('about.phil_2')}</span></h2>
            </div>
            <div className="space-y-6">
              {[
                { icon: Heart, title: t('about.v1_t'), desc: t('about.v1_d') },
                { icon: Shield, title: t('about.v2_t'), desc: t('about.v2_d') },
                { icon: Star, title: t('about.v3_t'), desc: t('about.v3_d') },
              ].map((item, i) => (
                <motion.div key={item.title} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                  className="flex gap-5">
                  <div className="w-12 h-12 rounded-full bg-[#121215] border border-[rgba(212,175,55,0.2)] flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-lg font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-2">{item.title}</h3>
                    <p className="text-sm text-[#A3A19C] leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Strip */}
      <section className="py-16 bg-[#121215] border-t border-[rgba(255,255,255,0.06)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex flex-wrap items-center gap-8">
            <a href="tel:+97333216371" className="flex items-center gap-2 text-sm text-[#A3A19C] hover:text-[#F5F2EB] transition-colors duration-300">
              <Phone className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} /> <span className="ltr-iso">+973 3321 6371</span>
            </a>
            <a href="mailto:Princess4Fitness@gmail.com" className="flex items-center gap-2 text-sm text-[#A3A19C] hover:text-[#F5F2EB] transition-colors duration-300">
              <Mail className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} /> <span className="ltr-iso">Princess4Fitness@gmail.com</span>
            </a>
            <span className="flex items-center gap-2 text-sm text-[#A3A19C]">
              <MapPin className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} /> {t('common.location_value')}
            </span>
          </div>
          <Link to="/questionnaire" className="btn-primary text-sm" data-testid="about-cta">
            {t('nav.cta')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
